import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { getLandClassCrops, setLandClassRead } from "../api";
import type { Parcel, ParcelLandClassRead } from "../types";

// Skąd wziął się werdykt odrolnienia dla tej działki.
//
// Powód istnienia tego okna: werdykt bierze się z jednego z dwóch zupełnie różnych źródeł —
// twardego odczytu punktowego z usługi powiatu albo odczytu rysunku mapy przez AI — a o tym,
// które zadziałało, decyduje wyłącznie to, co dany powiat udostępnia. Użytkownik musi to
// widzieć, zanim podpisze wniosek (albo go nie złoży).

const METHOD_NOTE: Record<string, string> = {
  county_api:
    "Powiat odpowiada na pytanie „jaki kontur jest w tym punkcie”. To ta sama ewidencja, "
    + "z której robi się wypis — odczyt jest twardy.",
  map_ai:
    "Powiat rysuje kontury, ale nie podaje ich oznaczeń maszynowo. Klasę odczytano z rysunku "
    + "mapy, dlatego przy odczycie widać wycinki, na których go oparto.",
  manual: "Wartość ustalona ręcznie przez człowieka — bije odczyt automatyczny.",
  none: "Nie udało się ustalić klasy pod urządzeniem.",
};

function ReadCard({
  read, crops, parcelId, onDone,
}: {
  read: ParcelLandClassRead;
  crops: { key: string; caption: string; url: string; filename: string }[];
  parcelId: string;
  onDone: () => void;
}) {
  const isManual = read.method === "manual";
  // Gdy decyzja ręczna już jest, pole startuje z jej wartością — inaczej „Zapisz” po
  // drobnej korekcie komentarza wyczyściłoby ustalenie.
  const [value, setValue] = useState(isManual ? read.klasouzytek : "");
  const [note, setNote] = useState(read.manual_note || "");
  const [saving, setSaving] = useState(false);
  const needsPerson = read.confidence !== "certain" || !read.cross_checked || read.near_boundary;

  const save = async (klasouzytek: string) => {
    setSaving(true);
    try {
      await setLandClassRead(parcelId, { read_id: read.id, klasouzytek, note });
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[var(--color-border)] rounded p-3 flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-sm">{read.device_type}</span>
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            dz. {read.parcel_teryt}
            {!read.in_project && " (działka spoza projektu)"}
          </span>
        </div>
        <span className="font-mono text-base">{read.klasouzytek || "brak konturu"}</span>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-[var(--color-text-muted)]">Metoda</dt>
        <dd>
          {read.method_label}
          {read.ai_model && <span className="text-[var(--color-text-muted)]"> · {read.ai_model}</span>}
        </dd>
        <dt className="text-[var(--color-text-muted)]">Dlaczego ta</dt>
        <dd>
          {read.method_reason || METHOD_NOTE[read.method] || "—"}
          {METHOD_NOTE[read.method] && read.method_reason && (
            <div className="text-[var(--color-text-muted)] mt-0.5">{METHOD_NOTE[read.method]}</div>
          )}
        </dd>
        {read.source_detail && (
          <>
            <dt className="text-[var(--color-text-muted)]">Odczyt</dt>
            <dd>{read.source_detail}</dd>
          </>
        )}
        <dt className="text-[var(--color-text-muted)]">Kontrola</dt>
        <dd>
          {read.cross_checked ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 size={12} />
              {read.method === "county_api"
                ? "źródłem jest sama ewidencja powiatu"
                : `zgodny z wykazem klasoużytków działki${read.parcel_contours ? ` (${read.parcel_contours})` : ""}`}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
              <AlertTriangle size={12} />
              brak wykazu klasoużytków do porównania — odczyt niezweryfikowany
            </span>
          )}
        </dd>
        {read.near_boundary && (
          <>
            <dt className="text-[var(--color-text-muted)]">Uwaga</dt>
            <dd className="text-amber-700 dark:text-amber-300">
              Obiekt stoi przy granicy (do 0,5 m). Słup liczy się na działce ze swojego punktu;
              złącze i trafo — na obu.
            </dd>
          </>
        )}
        {read.ai_note && (
          <>
            <dt className="text-[var(--color-text-muted)]">Uzasadnienie</dt>
            <dd className="italic">{read.ai_note}</dd>
          </>
        )}
        {read.source_url && (
          <>
            <dt className="text-[var(--color-text-muted)]">Źródło</dt>
            <dd>
              <a
                href={read.source_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[var(--color-accent)] hover:underline break-all"
              >
                {read.source_url} <ExternalLink size={11} />
              </a>
            </dd>
          </>
        )}
      </dl>

      {crops.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {crops.map((c) => (
            <a key={c.key} href={c.url} target="_blank" rel="noreferrer" className="block" title={c.caption}>
              <img
                src={c.url}
                alt={c.caption}
                className="w-40 h-40 object-cover border border-[var(--color-border)] rounded bg-white"
              />
              <span className="block w-40 text-[10px] text-[var(--color-text-muted)] mt-0.5">{c.caption}</span>
            </a>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 border-t border-[var(--color-border)] pt-2">
        <div className="flex-1 min-w-[10rem]">
          <label className="block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] mb-0.5">
            Klasoużytek ustalony ręcznie
          </label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={needsPerson ? "np. RIVa — wpisz, jeśli automat nie rozstrzygnął" : "nadpisz odczyt"}
          />
        </div>
        <div className="flex-1 min-w-[10rem]">
          <label className="block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] mb-0.5">
            Komentarz
          </label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="skąd wiadomo" />
        </div>
        <Button size="sm" loading={saving} disabled={saving || !value.trim()} onClick={() => save(value.trim())}>
          Zapisz decyzję
        </Button>
        {isManual && (
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            onClick={() => save("")}
            title="Wycofaj decyzję ręczną — sprawa wraca do automatu"
          >
            Wycofaj
          </Button>
        )}
      </div>
    </div>
  );
}

export function LandClassSourceModal({
  parcel, connectionId, onClose,
}: { parcel: Parcel | null; connectionId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [crops, setCrops] = useState<Record<string, { key: string; caption: string; url: string; filename: string }[]>>({});
  const source = parcel?.odrolnienie?.source;
  const reads = useMemo(() => source?.reads ?? [], [source]);

  // Adresy wycinków są podpisane i wygasają — pobieramy je dopiero tutaj, przy otwarciu.
  useEffect(() => {
    if (!parcel || !reads.some((r) => r.map_crops.length > 0)) return;
    let alive = true;
    getLandClassCrops(parcel.id)
      .then((rows) => {
        if (!alive) return;
        setCrops(Object.fromEntries(rows.map((r) => [r.read_id, r.crops])));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [parcel, reads]);

  if (!parcel) return null;
  const odr = parcel.odrolnienie;

  return (
    <Modal open onClose={onClose} title={`Skąd werdykt odrolnienia — dz. ${parcel.teryt}`} size="lg">
      <div className="flex flex-col gap-4">
        <div
          className={`border rounded p-3 text-sm ${
            odr?.required
              ? "border-red-300 bg-red-50 dark:bg-red-950/30"
              : odr?.manual
                ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30"
                : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30"
          }`}
        >
          <div className="font-medium">
            {odr?.required ? "Wymaga odrolnienia" : odr?.manual ? "Decyzja ręczna" : "Nie wymaga odrolnienia"}
          </div>
          <div className="mt-1">{odr?.reason}</div>
          {source && (
            <div className="mt-2 text-xs text-[var(--color-text-muted)]">
              Podstawa: <b>{source.method_label}</b>. {source.why}
            </div>
          )}
        </div>

        {reads.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Dla tej działki nie ma jeszcze odczytu klasy pod urządzeniem — werdykt opiera się na
            wykazie klasoużytków całej działki, który nie rozstrzyga, na którym z nich stoi obiekt.
            Uruchom „Klasa pod urządzeniami” na liście działek.
          </p>
        ) : (
          reads.map((read) => (
            <ReadCard
              key={read.id}
              read={read}
              crops={crops[read.id] ?? []}
              parcelId={parcel.id}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ["pp", "parcels", connectionId] });
                onClose();
              }}
            />
          ))
        )}
      </div>
    </Modal>
  );
}
